import React, { useState, useEffect } from 'react';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Loader2, Languages, BookOpen } from 'lucide-react';
import { getWordByWord } from '../services/tafsirService';

const WordByWordCard = ({ surahNumber, ayahNumber }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Auto-load when component mounts
    fetchWordByWord();
  }, [surahNumber, ayahNumber]);

  const fetchWordByWord = async () => {
    if (!surahNumber || !ayahNumber) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getWordByWord(surahNumber, ayahNumber);
      setData(result);
    } catch (err) {
      console.error('Error fetching word by word:', err);
      setError('Gagal memuat data kata per kata');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Memuat analisis kata...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Languages className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{error}</p>
      </div>
    );
  }

  if (!data || !data.available || data.words.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Languages className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>Maaf, analisis kata per kata belum tersedia untuk ayat ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Languages className="h-4 w-4" />
          <span>{data.words.length} kata</span>
        </div>
      </div>

      <ScrollArea className="h-[400px] w-full rounded-md border">
        <div className="p-4 space-y-3">
          {data.words.map((word, index) => (
            <div
              key={word.word_id || index}
              className="group relative rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                {/* Word Number */}
                <div className="flex-shrink-0">
                  <Badge variant="outline" className="text-xs">
                    {word.word_position}
                  </Badge>
                </div>

                {/* Word Content */}
                <div className="flex-1 space-y-2">
                  {/* Arabic Word */}
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-arabic" dir="rtl">
                      {word.text_arabic}
                    </span>
                    {word.root_letters && (
                      <Badge variant="secondary" className="text-xs">
                        Root: {word.root_letters}
                      </Badge>
                    )}
                  </div>

                  {/* Transliteration & Translation */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="font-semibold text-muted-foreground">Latin: </span>
                      <span className="text-primary">{word.transliteration}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-muted-foreground">Arti: </span>
                      <span>{word.translation_indonesian}</span>
                    </div>
                  </div>

                  {/* Part of Speech */}
                  {word.part_of_speech && (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {word.part_of_speech}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>

              {/* Separator between words */}
              {index < data.words.length - 1 && (
                <Separator className="mt-3" />
              )}
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Legend */}
      <div className="text-xs text-muted-foreground space-y-1">
        <p><strong>Root:</strong> Huruf kata dasar (akar kata bahasa Arab)</p>
        <p><strong>Latin:</strong> Bacaan latin/transliterasi</p>
        <p><strong>Arti:</strong> Terjemahan kata dalam bahasa Indonesia</p>
      </div>
    </div>
  );
};

export default WordByWordCard;
